import type {
  CustomerAgreement,
  CustomerAssignee,
  CustomerDetail,
  CustomerListParams,
  CustomerListResponse,
  CustomerLookupItem,
  CustomerPayload,
  CustomerStatement,
} from '../types/customer';
import type { Office } from '../types/office';
import type {
  ExpenseCategory,
  ShipmentDocument,
  ShipmentFileDetail,
  ShipmentFileListParams,
  ShipmentFileListResponse,
  ShipmentFilePayload,
  ShipmentInvoicePayload,
} from '../types/shipment';
import type {
  CompanyExpenseCategory,
  CompanyExpenseDetail,
  CompanyExpenseListParams,
  CompanyExpenseListResponse,
  CompanyExpensePayload,
  CompanyExpensePayables,
  CompanyExpenseSummary,
} from '../types/company-expense';
import type {
  InvoiceCategory,
  InvoiceDetail,
  InvoiceListParams,
  InvoiceListResponse,
  InvoicePayload,
} from '../types/invoice';
import type {
  AutoAllocateParams,
  AutoAllocatePreview,
  AutoAllocateResult,
  InvoicePaymentContext,
  ManualMatchPayload,
  ManualMatchResult,
  PaymentDetail,
  PaymentListParams,
  PaymentListResponse,
  PaymentPayload,
} from '../types/payment';
import type { DashboardData } from '../types/dashboard';
import type {
  MydataConnection,
  MydataDocumentDetail,
  MydataDocumentsPage,
  MydataMonthPdfBundlePreview,
  MydataPeriodSummary,
  MydataSyncHistoryPage,
  MydataSyncJob,
  MydataSyncJobsPage,
  MydataYearSummary,
} from '../types/mydata';
import type {
  AgingDrilldown,
  AgingReport,
  AgreementVarianceReport,
  CategorySummaryReport,
  CompanyExpenseAgingReport,
  CompanyExpenseByVendorReport,
  CompanyExpensePaymentsReport,
  CompanyExpenseTypeAnalysisReport,
  CompanyExpenseYearlyMonthlyReport,
  CustomerBalancesReport,
  MonthlySummaryReport,
  ShipmentByCustomerReport,
  ShipmentByPortVesselReport,
  ShipmentContainerBlReport,
  ShipmentExpenseAnalysisReport,
  ShipmentExpenseCategoriesReport,
  ShipmentMonthlySummaryReport,
  ShipmentOpenFilesReport,
  UnallocatedPaymentsReport,
  YearlyMonthlyReport,
} from '../types/reports';
import { t } from '../i18n';

const API_BASE =
  process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'http://localhost:5000';

const TOKEN_KEY = 'balanceflow_token';
const USER_KEY = 'balanceflow_user';

export type AuthUser = {
  id: number;
  name: string;
  email: string;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'USER';
  tenantId: number | null;
  tenantName: string | null;
  tenantSetupCompleted: boolean;
  supportLogin?: boolean;
  features?: string[];
};

export type ManagedUser = {
  id: number;
  name: string;
  email: string | null;
  role: 'ADMIN' | 'USER';
  tenantId?: number | null;
  active: boolean;
  canLogin?: boolean;
  officeId?: number | null;
  office?: { id: number; name: string } | null;
  mustChangePassword?: boolean;
  inviteTokenExpiresAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type PlatformAdmin = {
  id: number;
  name: string;
  email: string;
  role: 'SUPER_ADMIN';
  active: boolean;
  mustChangePassword: boolean;
  inviteTokenExpiresAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type PlatformSettings = {
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string;
  smtpPassConfigured: boolean;
  mailFrom: string;
  mailConfigured: boolean;
  updatedAt: string;
};

export type AuditLogEntry = {
  id: number;
  actorUserId: number | null;
  actorEmail: string | null;
  actorRole: string | null;
  tenantId: number | null;
  action: string;
  entityType: string | null;
  entityId: number | null;
  summary: string;
  metadata: Record<string, unknown> | null;
  method: string | null;
  path: string | null;
  statusCode: number | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
};

export type AuditLogListResponse = {
  items: AuditLogEntry[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

export type PackageStatus = 'active' | 'upcoming' | 'expired' | 'disabled';

export type TenantPackageRenewal = {
  id: number;
  startsAt: string;
  endsAt: string;
  kind: string;
  notes: string | null;
  createdAt: string;
  createdById: number | null;
};

export type Tenant = {
  id: number;
  name: string;
  slug: string;
  vatNumber: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  notes: string | null;
  active: boolean;
  setupCompleted: boolean;
  packageStartsAt: string | null;
  packageEndsAt: string | null;
  packageStatus?: PackageStatus;
  renewalCount?: number;
  packageRenewals?: TenantPackageRenewal[];
  createdAt: string;
  updatedAt: string;
  _count?: {
    users: number;
    customers: number;
    packageRenewals?: number;
  };
  users?: Array<{
    id: number;
    name: string;
    email: string;
    mustChangePassword: boolean;
    inviteTokenExpiresAt: string | null;
  }>;
  admin?: {
    id: number;
    name: string;
    email: string;
    role: string;
    active: boolean;
    mustChangePassword?: boolean;
  };
  inviteUrl?: string;
  inviteExpiresAt?: string;
  inviteEmail?: {
    subject: string;
    plainText: string;
    html: string;
  };
};

export type InviteInfo = {
  email: string;
  name: string;
  tenantName: string | null;
  expiresAt: string | null;
};

export type AppFeature = {
  id: number;
  code: string;
  nameEl: string;
  sortOrder: number;
  alwaysOn: boolean;
};

export type AppPackage = {
  id: number;
  name: string;
  code: string;
  description: string | null;
  active: boolean;
  sortOrder: number;
  tenantCount: number;
  features: AppFeature[];
  enabled?: boolean;
  createdAt: string;
  updatedAt: string;
};

export type TenantAppPackagesResponse = {
  packages: AppPackage[];
  features: string[];
};

export type LoginResponse = {
  accessToken: string;
  user: AuthUser;
};

export class ApiError extends Error {
  status: number;
  code?: string;
  details?: Record<string, unknown>;

  constructor(
    message: string,
    status: number,
    extras?: { code?: string; details?: Record<string, unknown> },
  ) {
    super(message);
    this.status = status;
    this.code = extras?.code;
    this.details = extras?.details;
  }
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function setSession(token: string, user: AuthUser): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export const SESSION_EXPIRED_EVENT = 'balanceflow:session-expired';
const SESSION_EXPIRED_FLAG = 'balanceflow_session_expired';

export function markSessionExpired(): void {
  try {
    sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1');
  } catch {
    // ignore
  }
  clearSession();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  }
}

/** Returns true once if the previous request cleared the session due to 401. */
export function consumeSessionExpiredFlag(): boolean {
  try {
    const flag = sessionStorage.getItem(SESSION_EXPIRED_FLAG);
    if (flag) {
      sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json');
  }

  const token = getStoredToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const isLoginAttempt = path.startsWith('/auth/login');
    if (response.status === 401 && !isLoginAttempt && token) {
      markSessionExpired();
    }

    let message: string = t().common.error;
    let code: string | undefined;
    let details: Record<string, unknown> | undefined;
    try {
      const body = (await response.json()) as {
        message?: string | string[];
        code?: string;
        retryAfterSeconds?: number;
        remaining?: unknown;
        payment?: unknown;
      };
      if (Array.isArray(body.message)) {
        message = body.message.join(', ');
      } else if (typeof body.message === 'string') {
        message = body.message;
      }
      code = body.code;
      details = {};
      if (body.remaining != null) details.remaining = body.remaining;
      if (body.payment != null) details.payment = body.payment;
      if (body.retryAfterSeconds != null) {
        details.retryAfterSeconds = body.retryAfterSeconds;
      }
      if (Object.keys(details).length === 0) details = undefined;
    } catch {
      // keep default
    }

    if (response.status === 401 && !isLoginAttempt) {
      code = code || 'SESSION_EXPIRED';
      message = t().auth.sessionExpired;
    }

    if (response.status === 429 && code !== 'AADE_RATE_LIMIT') {
      message = t().auth.tooManyRequests;
      code = code || 'RATE_LIMITED';
    }

    throw new ApiError(message, response.status, { code, details });
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export const api = {
  login(email: string, password: string) {
    return request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  getMe() {
    return request<AuthUser>('/auth/me');
  },

  changePassword(currentPassword: string, newPassword: string) {
    return request<{ ok: true }>('/auth/me/password', {
      method: 'PATCH',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  forgotPassword(email: string) {
    return request<{ ok: true; message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  getPasswordReset(token: string) {
    return request<{
      email: string;
      name: string;
      expiresAt: string | null;
    }>(`/auth/reset-password/${encodeURIComponent(token)}`);
  },

  resetPassword(token: string, password: string) {
    return request<LoginResponse>(
      `/auth/reset-password/${encodeURIComponent(token)}`,
      {
        method: 'POST',
        body: JSON.stringify({ password }),
      },
    );
  },

  getPlatformAdmins() {
    return request<PlatformAdmin[]>('/platform/admins');
  },

  createPlatformAdmin(payload: {
    name: string;
    email: string;
    password?: string;
  }) {
    return request<{
      admin: PlatformAdmin;
      inviteUrl: string;
      inviteExpiresAt: string;
    }>('/platform/admins', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updatePlatformAdmin(
    id: number,
    payload: { name?: string; email?: string; active?: boolean },
  ) {
    return request<PlatformAdmin>(`/platform/admins/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  recreatePlatformAdminInvite(id: number) {
    return request<{
      admin: PlatformAdmin;
      inviteUrl: string;
      inviteExpiresAt: string;
    }>(`/platform/admins/${id}/invite`, {
      method: 'POST',
    });
  },

  getPlatformSettings() {
    return request<PlatformSettings>('/platform/settings');
  },

  updatePlatformSettings(payload: {
    smtpHost?: string | null;
    smtpPort?: number;
    smtpSecure?: boolean;
    smtpUser?: string | null;
    smtpPass?: string;
    mailFrom?: string | null;
  }) {
    return request<PlatformSettings>('/platform/settings', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  getAuditLogs(params: {
    page?: number;
    pageSize?: number;
    action?: string;
    entityType?: string;
    tenantId?: number;
    actorEmail?: string;
    from?: string;
    to?: string;
    search?: string;
  } = {}) {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    if (params.action) qs.set('action', params.action);
    if (params.entityType) qs.set('entityType', params.entityType);
    if (params.tenantId) qs.set('tenantId', String(params.tenantId));
    if (params.actorEmail) qs.set('actorEmail', params.actorEmail);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.search) qs.set('search', params.search);
    const query = qs.toString();
    return request<AuditLogListResponse>(
      `/platform/audit-logs${query ? `?${query}` : ''}`,
    );
  },

  getAuditLogFilters() {
    return request<{ actions: string[]; entityTypes: string[] }>(
      '/platform/audit-logs/filters',
    );
  },

  getTenants() {
    return request<Tenant[]>('/tenants');
  },

  getAppFeatures() {
    return request<AppFeature[]>('/app-features');
  },

  getAppPackages() {
    return request<AppPackage[]>('/app-packages');
  },

  getAppPackage(id: number) {
    return request<AppPackage>(`/app-packages/${id}`);
  },

  createAppPackage(payload: {
    name: string;
    code: string;
    description?: string;
    active?: boolean;
    sortOrder?: number;
    featureCodes?: string[];
  }) {
    return request<AppPackage>('/app-packages', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updateAppPackage(
    id: number,
    payload: {
      name?: string;
      code?: string;
      description?: string | null;
      active?: boolean;
      sortOrder?: number;
    },
  ) {
    return request<AppPackage>(`/app-packages/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  setAppPackageFeatures(id: number, featureCodes: string[]) {
    return request<AppPackage>(`/app-packages/${id}/features`, {
      method: 'PUT',
      body: JSON.stringify({ featureCodes }),
    });
  },

  getTenantAppPackages(tenantId: number) {
    return request<TenantAppPackagesResponse>(
      `/tenants/${tenantId}/app-packages`,
    );
  },

  setTenantAppPackages(tenantId: number, packageIds: number[]) {
    return request<TenantAppPackagesResponse>(
      `/tenants/${tenantId}/app-packages`,
      {
        method: 'PUT',
        body: JSON.stringify({ packageIds }),
      },
    );
  },

  createTenant(payload: {
    name: string;
    slug: string;
    admin: { name: string; email: string; password?: string };
    packageStartsAt?: string;
    packageEndsAt?: string;
  }) {
    return request<Tenant>('/tenants', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  recreateTenantInvite(id: number) {
    return request<{
      tenantId: number;
      admin: { id: number; name: string; email: string };
      inviteUrl: string;
      inviteExpiresAt: string;
      inviteEmail?: {
        subject: string;
        plainText: string;
        html: string;
      };
    }>(`/tenants/${id}/invite`, {
      method: 'POST',
    });
  },

  sendTenantInviteEmail(id: number) {
    return request<{ ok: true; email: string }>(`/tenants/${id}/invite/send`, {
      method: 'POST',
    });
  },

  supportLogin(tenantId: number) {
    return request<LoginResponse>(`/tenants/${tenantId}/support-login`, {
      method: 'POST',
    });
  },

  exitSupportLogin() {
    return request<LoginResponse>('/auth/exit-support-login', {
      method: 'POST',
    });
  },

  renewTenantPackage(
    id: number,
    payload: {
      packageStartsAt?: string;
      packageEndsAt?: string;
      notes?: string;
    } = {},
  ) {
    return request<Tenant>(`/tenants/${id}/renew`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  createTenantPackage(
    id: number,
    payload: { packageStartsAt: string; packageEndsAt: string },
  ) {
    return request<Tenant>(`/tenants/${id}/renewals`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updateTenantPackage(
    tenantId: number,
    renewalId: number,
    payload: { packageStartsAt: string; packageEndsAt: string },
  ) {
    return request<Tenant>(`/tenants/${tenantId}/renewals/${renewalId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  getTenantRenewals(id: number) {
    return request<TenantPackageRenewal[]>(`/tenants/${id}/renewals`);
  },

  deleteTenantRenewal(tenantId: number, renewalId: number) {
    return request<Tenant>(`/tenants/${tenantId}/renewals/${renewalId}`, {
      method: 'DELETE',
    });
  },

  getInvite(token: string) {
    return request<InviteInfo>(`/auth/invite/${encodeURIComponent(token)}`);
  },

  acceptInvite(token: string, password: string) {
    return request<LoginResponse>(
      `/auth/invite/${encodeURIComponent(token)}/accept`,
      {
        method: 'POST',
        body: JSON.stringify({ password }),
      },
    );
  },

  updateTenant(
    id: number,
    payload: {
      name?: string;
      active?: boolean;
      packageStartsAt?: string;
      packageEndsAt?: string;
    },
  ) {
    return request<Tenant>(`/tenants/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  getMyTenant() {
    return request<Tenant>('/tenants/me');
  },

  completeTenantSetup(payload: {
    name: string;
    vatNumber?: string;
    email?: string;
    phone?: string;
    address?: string;
    notes?: string;
  }) {
    return request<Tenant>('/tenants/me/setup', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  getUsers() {
    return request<ManagedUser[]>('/users');
  },

  createUser(payload: {
    name: string;
    email: string;
    password: string;
    role?: 'ADMIN' | 'USER';
    officeId?: number | null;
  }) {
    return request<ManagedUser>('/users', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  createUserWithoutLogin(payload: {
    name: string;
    officeId?: number | null;
  }) {
    return request<ManagedUser>('/users/without-login', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  inviteUser(payload: {
    name: string;
    email: string;
    role?: 'ADMIN' | 'USER';
    officeId?: number | null;
  }) {
    return request<{
      user: ManagedUser;
      inviteUrl: string;
      inviteExpiresAt: string;
      emailSent: boolean;
    }>('/users/invite', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  recreateUserInvite(
    id: number,
    payload: { email?: string; role?: 'ADMIN' | 'USER' } = {},
  ) {
    return request<{
      user: ManagedUser;
      inviteUrl: string;
      inviteExpiresAt: string;
      emailSent: boolean;
    }>(`/users/${id}/invite`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  deleteUser(id: number) {
    return request<{ ok: true }>(`/users/${id}`, {
      method: 'DELETE',
    });
  },

  updateUser(
    id: number,
    payload: {
      name?: string;
      email?: string;
      password?: string;
      role?: 'ADMIN' | 'USER';
      active?: boolean;
      officeId?: number | null;
    },
  ) {
    return request<ManagedUser>(`/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  getCustomers(params: CustomerListParams = {}) {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.active) query.set('active', params.active);
    if (params.accountingBooksCategory) {
      query.set('accountingBooksCategory', params.accountingBooksCategory);
    }
    if (params.officeId) query.set('officeId', params.officeId);
    if (params.assignedUserId) {
      query.set('assignedUserId', params.assignedUserId);
    }
    if (params.page) query.set('page', String(params.page));
    if (params.pageSize) query.set('pageSize', String(params.pageSize));
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortDir) query.set('sortDir', params.sortDir);
    const qs = query.toString();
    return request<CustomerListResponse>(`/customers${qs ? `?${qs}` : ''}`);
  },

  getCustomerAssignees() {
    return request<CustomerAssignee[]>('/customers/assignees');
  },

  getCustomerLookup(params: { active?: string } = {}) {
    const query = new URLSearchParams();
    if (params.active) query.set('active', params.active);
    const qs = query.toString();
    return request<CustomerLookupItem[]>(
      `/customers/lookup${qs ? `?${qs}` : ''}`,
    );
  },

  getCustomer(id: number) {
    return request<CustomerDetail>(`/customers/${id}`);
  },

  createCustomer(payload: CustomerPayload) {
    return request<CustomerDetail>('/customers', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updateCustomer(id: number, payload: CustomerPayload) {
    return request<CustomerDetail>(`/customers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  deleteCustomer(id: number) {
    return request<{ id: number; deleted: boolean }>(`/customers/${id}`, {
      method: 'DELETE',
    });
  },

  getCustomerAgreement(id: number) {
    return request<CustomerAgreement | null>(`/customers/${id}/agreement`);
  },

  getCustomerStatement(id: number) {
    return request<CustomerStatement>(`/customers/${id}/statement`);
  },

  getInvoiceCategories(activeOnly = false) {
    const qs = activeOnly ? '?activeOnly=true' : '';
    return request<InvoiceCategory[]>(`/invoice-categories${qs}`);
  },

  createInvoiceCategory(payload: {
    name: string;
    description?: string;
    active?: boolean;
  }) {
    return request<InvoiceCategory>('/invoice-categories', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updateInvoiceCategory(
    id: number,
    payload: { name?: string; description?: string; active?: boolean },
  ) {
    return request<InvoiceCategory>(`/invoice-categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  deactivateInvoiceCategory(id: number) {
    return request<InvoiceCategory>(`/invoice-categories/${id}/deactivate`, {
      method: 'PATCH',
    });
  },

  getOffices(activeOnly = false) {
    const qs = activeOnly ? '?activeOnly=true' : '';
    return request<Office[]>(`/offices${qs}`);
  },

  createOffice(payload: { name: string; active?: boolean }) {
    return request<Office>('/offices', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updateOffice(
    id: number,
    payload: { name?: string; active?: boolean },
  ) {
    return request<Office>(`/offices/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  deactivateOffice(id: number) {
    return request<Office>(`/offices/${id}/deactivate`, {
      method: 'PATCH',
    });
  },

  getExpenseCategories(activeOnly = false) {
    const qs = activeOnly ? '?activeOnly=true' : '';
    return request<ExpenseCategory[]>(`/expense-categories${qs}`);
  },

  createExpenseCategory(payload: {
    name: string;
    description?: string;
    active?: boolean;
  }) {
    return request<ExpenseCategory>('/expense-categories', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updateExpenseCategory(
    id: number,
    payload: { name?: string; description?: string; active?: boolean },
  ) {
    return request<ExpenseCategory>(`/expense-categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  deactivateExpenseCategory(id: number) {
    return request<ExpenseCategory>(`/expense-categories/${id}/deactivate`, {
      method: 'PATCH',
    });
  },

  getCompanyExpenseCategories(activeOnly = false) {
    const qs = activeOnly ? '?activeOnly=true' : '';
    return request<CompanyExpenseCategory[]>(
      `/company-expense-categories${qs}`,
    );
  },

  createCompanyExpenseCategory(payload: {
    name: string;
    description?: string;
    active?: boolean;
  }) {
    return request<CompanyExpenseCategory>('/company-expense-categories', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updateCompanyExpenseCategory(
    id: number,
    payload: { name?: string; description?: string; active?: boolean },
  ) {
    return request<CompanyExpenseCategory>(
      `/company-expense-categories/${id}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
    );
  },

  deactivateCompanyExpenseCategory(id: number) {
    return request<CompanyExpenseCategory>(
      `/company-expense-categories/${id}/deactivate`,
      { method: 'PATCH' },
    );
  },

  getCompanyExpenses(params: CompanyExpenseListParams = {}) {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.categoryId) query.set('categoryId', String(params.categoryId));
    if (params.type) query.set('type', params.type);
    if (params.status) query.set('status', params.status);
    if (params.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params.dateTo) query.set('dateTo', params.dateTo);
    if (params.page) query.set('page', String(params.page));
    if (params.pageSize) query.set('pageSize', String(params.pageSize));
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortDir) query.set('sortDir', params.sortDir);
    const qs = query.toString();
    return request<CompanyExpenseListResponse>(
      `/company-expenses${qs ? `?${qs}` : ''}`,
    );
  },

  getCompanyExpense(id: number) {
    return request<CompanyExpenseDetail>(`/company-expenses/${id}`);
  },

  createCompanyExpense(payload: CompanyExpensePayload) {
    return request<CompanyExpenseDetail>('/company-expenses', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updateCompanyExpense(id: number, payload: Partial<CompanyExpensePayload>) {
    return request<CompanyExpenseDetail>(`/company-expenses/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  deleteCompanyExpense(id: number) {
    return request<{ ok: boolean }>(`/company-expenses/${id}`, {
      method: 'DELETE',
    });
  },

  getCompanyExpensePayables() {
    return request<CompanyExpensePayables>('/company-expenses/payables');
  },

  getCompanyExpenseSummary(params: { month?: number; year?: number } = {}) {
    const query = new URLSearchParams();
    if (params.month) query.set('month', String(params.month));
    if (params.year) query.set('year', String(params.year));
    const qs = query.toString();
    return request<CompanyExpenseSummary>(
      `/company-expenses/summary${qs ? `?${qs}` : ''}`,
    );
  },

  getShipmentFiles(params: ShipmentFileListParams = {}) {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.customerId) query.set('customerId', String(params.customerId));
    if (params.status) query.set('status', params.status);
    if (params.page) query.set('page', String(params.page));
    if (params.pageSize) query.set('pageSize', String(params.pageSize));
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortDir) query.set('sortDir', params.sortDir);
    const qs = query.toString();
    return request<ShipmentFileListResponse>(
      `/shipment-files${qs ? `?${qs}` : ''}`,
    );
  },

  getShipmentFile(id: number) {
    return request<ShipmentFileDetail>(`/shipment-files/${id}`);
  },

  createShipmentFile(payload: ShipmentFilePayload) {
    return request<ShipmentFileDetail>('/shipment-files', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updateShipmentFile(id: number, payload: Partial<ShipmentFilePayload>) {
    return request<ShipmentFileDetail>(`/shipment-files/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  deleteShipmentFile(id: number) {
    return request<{ id: number; isDeleted: boolean }>(
      `/shipment-files/${id}`,
      { method: 'DELETE' },
    );
  },

  createShipmentInvoice(id: number, payload: ShipmentInvoicePayload) {
    return request<ShipmentFileDetail>(`/shipment-files/${id}/invoice`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async uploadShipmentDocument(id: number, file: File) {
    const headers = new Headers();
    const token = getStoredToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    const body = new FormData();
    body.append('file', file);
    const response = await fetch(`${API_BASE}/shipment-files/${id}/documents`, {
      method: 'POST',
      headers,
      body,
    });
    if (!response.ok) {
      let message: string = t().common.error;
      try {
        const data = (await response.json()) as { message?: string | string[] };
        if (Array.isArray(data.message)) message = data.message.join(', ');
        else if (typeof data.message === 'string') message = data.message;
      } catch {
        // keep default
      }
      throw new ApiError(message, response.status);
    }
    return (await response.json()) as ShipmentDocument;
  },

  async downloadShipmentDocument(
    shipmentFileId: number,
    documentId: number,
    originalName: string,
  ) {
    const headers = new Headers();
    const token = getStoredToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    const response = await fetch(
      `${API_BASE}/shipment-files/${shipmentFileId}/documents/${documentId}`,
      { headers },
    );
    if (!response.ok) {
      throw new ApiError(t().common.error, response.status);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = originalName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  },

  deleteShipmentDocument(shipmentFileId: number, documentId: number) {
    return request<{ id: number; deleted: boolean }>(
      `/shipment-files/${shipmentFileId}/documents/${documentId}`,
      { method: 'DELETE' },
    );
  },

  getInvoices(params: InvoiceListParams = {}) {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.customerId) query.set('customerId', String(params.customerId));
    if (params.categoryId) query.set('categoryId', String(params.categoryId));
    if (params.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params.dateTo) query.set('dateTo', params.dateTo);
    if (params.status) query.set('status', params.status);
    if (params.page) query.set('page', String(params.page));
    if (params.pageSize) query.set('pageSize', String(params.pageSize));
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortDir) query.set('sortDir', params.sortDir);
    const qs = query.toString();
    return request<InvoiceListResponse>(`/invoices${qs ? `?${qs}` : ''}`);
  },

  getInvoice(id: number) {
    return request<InvoiceDetail>(`/invoices/${id}`);
  },

  createInvoice(payload: InvoicePayload) {
    return request<InvoiceDetail>('/invoices', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updateInvoice(id: number, payload: Partial<InvoicePayload>) {
    return request<InvoiceDetail>(`/invoices/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  deleteInvoice(id: number) {
    return request<{ id: number; isDeleted: boolean }>(`/invoices/${id}`, {
      method: 'DELETE',
    });
  },

  getPayments(params: PaymentListParams = {}) {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.customerId) query.set('customerId', String(params.customerId));
    if (params.invoiceId) query.set('invoiceId', String(params.invoiceId));
    if (params.unallocatedOnly) query.set('unallocatedOnly', 'true');
    if (params.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params.dateTo) query.set('dateTo', params.dateTo);
    if (params.page) query.set('page', String(params.page));
    if (params.pageSize) query.set('pageSize', String(params.pageSize));
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortDir) query.set('sortDir', params.sortDir);
    const qs = query.toString();
    return request<PaymentListResponse>(`/payments${qs ? `?${qs}` : ''}`);
  },

  getPayment(id: number) {
    return request<PaymentDetail>(`/payments/${id}`);
  },

  getInvoicePaymentContext(invoiceId: number, excludePaymentId?: number) {
    const qs = excludePaymentId
      ? `?excludePaymentId=${excludePaymentId}`
      : '';
    return request<InvoicePaymentContext>(
      `/payments/invoice-context/${invoiceId}${qs}`,
    );
  },

  createPayment(payload: PaymentPayload) {
    return request<PaymentDetail>('/payments', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updatePayment(id: number, payload: Partial<PaymentPayload>) {
    return request<PaymentDetail>(`/payments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  deletePayment(id: number) {
    return request<{ id: number; isDeleted: boolean }>(`/payments/${id}`, {
      method: 'DELETE',
    });
  },

  previewPaymentAutoAllocate(payload: AutoAllocateParams) {
    return request<AutoAllocatePreview>('/payments/auto-allocate/preview', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  applyPaymentAutoAllocate(payload: AutoAllocateParams) {
    return request<AutoAllocateResult>('/payments/auto-allocate', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  applyManualMatch(payload: ManualMatchPayload) {
    return request<ManualMatchResult>('/payments/manual-match', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getMonthlySummary(month: number, year: number, officeId?: string) {
    const query = new URLSearchParams({
      month: String(month),
      year: String(year),
    });
    if (officeId) query.set('officeId', officeId);
    return request<MonthlySummaryReport>(
      `/reports/monthly-summary?${query.toString()}`,
    );
  },

  getYearlyMonthlyReport(year: number, officeId?: string) {
    const query = new URLSearchParams({ year: String(year) });
    if (officeId) query.set('officeId', officeId);
    return request<YearlyMonthlyReport>(
      `/reports/yearly-monthly?${query.toString()}`,
    );
  },

  getCustomerBalancesReport(params: {
    customerId?: number;
    activeOnly?: boolean;
    balanceFilter?: 'positive' | 'zero' | 'negative' | '';
    accountingBooksCategory?: string;
    officeId?: string;
    assignedUserId?: string;
  } = {}) {
    const query = new URLSearchParams();
    if (params.customerId) query.set('customerId', String(params.customerId));
    if (params.activeOnly) query.set('activeOnly', 'true');
    if (params.balanceFilter) query.set('balanceFilter', params.balanceFilter);
    if (params.accountingBooksCategory) {
      query.set('accountingBooksCategory', params.accountingBooksCategory);
    }
    if (params.officeId) query.set('officeId', params.officeId);
    if (params.assignedUserId) query.set('assignedUserId', params.assignedUserId);
    const qs = query.toString();
    return request<CustomerBalancesReport>(
      `/reports/customer-balances${qs ? `?${qs}` : ''}`,
    );
  },

  getAgingReport(params: { officeId?: string; assignedUserId?: string } = {}) {
    const query = new URLSearchParams();
    if (params.officeId) query.set('officeId', params.officeId);
    if (params.assignedUserId) query.set('assignedUserId', params.assignedUserId);
    const qs = query.toString();
    return request<AgingReport>(`/reports/aging${qs ? `?${qs}` : ''}`);
  },

  getAgingDrilldown(customerId: number, bucket?: string) {
    const qs = bucket && bucket !== 'all' ? `?bucket=${bucket}` : '';
    return request<AgingDrilldown>(`/reports/aging/${customerId}${qs}`);
  },

  getCategorySummaryReport(params: {
    dateFrom?: string;
    dateTo?: string;
    customerId?: number;
    categoryId?: number;
  } = {}) {
    const query = new URLSearchParams();
    if (params.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params.dateTo) query.set('dateTo', params.dateTo);
    if (params.customerId) query.set('customerId', String(params.customerId));
    if (params.categoryId) query.set('categoryId', String(params.categoryId));
    const qs = query.toString();
    return request<CategorySummaryReport>(
      `/reports/category-summary${qs ? `?${qs}` : ''}`,
    );
  },

  getUnallocatedPaymentsReport(params: {
    dateFrom?: string;
    dateTo?: string;
    customerId?: number;
    activeOnly?: boolean;
  } = {}) {
    const query = new URLSearchParams();
    if (params.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params.dateTo) query.set('dateTo', params.dateTo);
    if (params.customerId) query.set('customerId', String(params.customerId));
    if (params.activeOnly) query.set('activeOnly', 'true');
    const qs = query.toString();
    return request<UnallocatedPaymentsReport>(
      `/reports/unallocated-payments${qs ? `?${qs}` : ''}`,
    );
  },

  getAgreementVarianceReport(params: {
    year: number;
    month?: number;
    customerId?: number;
    activeOnly?: boolean;
    varianceFilter?: 'positive' | 'zero' | 'negative' | 'nonzero' | '';
  }) {
    const query = new URLSearchParams();
    query.set('year', String(params.year));
    if (params.month) query.set('month', String(params.month));
    if (params.customerId) query.set('customerId', String(params.customerId));
    if (params.activeOnly) query.set('activeOnly', 'true');
    if (params.varianceFilter) query.set('varianceFilter', params.varianceFilter);
    const qs = query.toString();
    return request<AgreementVarianceReport>(
      `/reports/agreement-variance?${qs}`,
    );
  },

  getShipmentMonthlySummary(month: number, year: number) {
    return request<ShipmentMonthlySummaryReport>(
      `/reports/shipment-monthly-summary?month=${month}&year=${year}`,
    );
  },

  getShipmentExpenseCategoriesReport(params: {
    dateFrom?: string;
    dateTo?: string;
    customerId?: number;
    categoryId?: number;
  } = {}) {
    const query = new URLSearchParams();
    if (params.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params.dateTo) query.set('dateTo', params.dateTo);
    if (params.customerId) query.set('customerId', String(params.customerId));
    if (params.categoryId) query.set('categoryId', String(params.categoryId));
    const qs = query.toString();
    return request<ShipmentExpenseCategoriesReport>(
      `/reports/shipment-expense-categories${qs ? `?${qs}` : ''}`,
    );
  },

  getShipmentOpenFilesReport(params: {
    customerId?: number;
    port?: string;
  } = {}) {
    const query = new URLSearchParams();
    if (params.customerId) query.set('customerId', String(params.customerId));
    if (params.port) query.set('port', params.port);
    const qs = query.toString();
    return request<ShipmentOpenFilesReport>(
      `/reports/shipment-open-files${qs ? `?${qs}` : ''}`,
    );
  },

  getShipmentByCustomerReport(params: {
    dateFrom?: string;
    dateTo?: string;
    customerId?: number;
  } = {}) {
    const query = new URLSearchParams();
    if (params.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params.dateTo) query.set('dateTo', params.dateTo);
    if (params.customerId) query.set('customerId', String(params.customerId));
    const qs = query.toString();
    return request<ShipmentByCustomerReport>(
      `/reports/shipment-by-customer${qs ? `?${qs}` : ''}`,
    );
  },

  getShipmentContainerBlReport(q: string) {
    return request<ShipmentContainerBlReport>(
      `/reports/shipment-container-bl?q=${encodeURIComponent(q)}`,
    );
  },

  getShipmentExpenseAnalysisReport(params: {
    dateFrom?: string;
    dateTo?: string;
    customerId?: number;
  } = {}) {
    const query = new URLSearchParams();
    if (params.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params.dateTo) query.set('dateTo', params.dateTo);
    if (params.customerId) query.set('customerId', String(params.customerId));
    const qs = query.toString();
    return request<ShipmentExpenseAnalysisReport>(
      `/reports/shipment-expense-analysis${qs ? `?${qs}` : ''}`,
    );
  },

  getShipmentByPortVesselReport(params: {
    dateFrom?: string;
    dateTo?: string;
    groupBy?: 'port' | 'vessel';
  } = {}) {
    const query = new URLSearchParams();
    if (params.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params.dateTo) query.set('dateTo', params.dateTo);
    if (params.groupBy) query.set('groupBy', params.groupBy);
    const qs = query.toString();
    return request<ShipmentByPortVesselReport>(
      `/reports/shipment-by-port-vessel${qs ? `?${qs}` : ''}`,
    );
  },

  getCompanyExpenseYearlyMonthlyReport(year?: number) {
    const query = new URLSearchParams();
    if (year) query.set('year', String(year));
    const qs = query.toString();
    return request<CompanyExpenseYearlyMonthlyReport>(
      `/reports/company-expense-yearly-monthly${qs ? `?${qs}` : ''}`,
    );
  },

  getCompanyExpenseByVendorReport(params: {
    dateFrom?: string;
    dateTo?: string;
    categoryId?: number;
  } = {}) {
    const query = new URLSearchParams();
    if (params.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params.dateTo) query.set('dateTo', params.dateTo);
    if (params.categoryId) query.set('categoryId', String(params.categoryId));
    const qs = query.toString();
    return request<CompanyExpenseByVendorReport>(
      `/reports/company-expense-by-vendor${qs ? `?${qs}` : ''}`,
    );
  },

  getCompanyExpenseTypeAnalysisReport(params: {
    dateFrom?: string;
    dateTo?: string;
  } = {}) {
    const query = new URLSearchParams();
    if (params.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params.dateTo) query.set('dateTo', params.dateTo);
    const qs = query.toString();
    return request<CompanyExpenseTypeAnalysisReport>(
      `/reports/company-expense-type-analysis${qs ? `?${qs}` : ''}`,
    );
  },

  getCompanyExpensePaymentsReport(params: {
    dateFrom?: string;
    dateTo?: string;
    categoryId?: number;
  } = {}) {
    const query = new URLSearchParams();
    if (params.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params.dateTo) query.set('dateTo', params.dateTo);
    if (params.categoryId) query.set('categoryId', String(params.categoryId));
    const qs = query.toString();
    return request<CompanyExpensePaymentsReport>(
      `/reports/company-expense-payments${qs ? `?${qs}` : ''}`,
    );
  },

  getCompanyExpenseAgingReport() {
    return request<CompanyExpenseAgingReport>(
      '/reports/company-expense-aging',
    );
  },

  getMydataConnection() {
    return request<MydataConnection>('/mydata/connection');
  },

  upsertMydataConnection(payload: {
    aadeUserId: string;
    subscriptionKey?: string;
    environment: 'sandbox' | 'production';
  }) {
    return request<MydataConnection>('/mydata/connection', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  syncMydata(payload: { dateFrom: string; dateTo: string }) {
    return request<MydataPeriodSummary>('/mydata/sync', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getMydataSyncHistory(params: { limit?: number } = {}) {
    const query = new URLSearchParams();
    if (params.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return request<MydataSyncHistoryPage>(
      `/mydata/sync-history${qs ? `?${qs}` : ''}`,
    );
  },

  createMydataSyncJob(payload: { year: number; months?: number[] }) {
    return request<MydataSyncJob>('/mydata/sync-jobs', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getMydataSyncJobs(params: { limit?: number } = {}) {
    const query = new URLSearchParams();
    if (params.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return request<MydataSyncJobsPage>(
      `/mydata/sync-jobs${qs ? `?${qs}` : ''}`,
    );
  },

  getMydataSyncJob(id: number) {
    return request<MydataSyncJob>(`/mydata/sync-jobs/${id}`);
  },

  cancelMydataSyncJob(id: number) {
    return request<MydataSyncJob>(`/mydata/sync-jobs/${id}/cancel`, {
      method: 'POST',
    });
  },

  getMydataSummary(params: { dateFrom: string; dateTo: string }) {
    const query = new URLSearchParams();
    query.set('dateFrom', params.dateFrom);
    query.set('dateTo', params.dateTo);
    return request<MydataPeriodSummary>(`/mydata/summary?${query.toString()}`);
  },

  getMydataYearSummary(params: { year: number }) {
    const query = new URLSearchParams();
    query.set('year', String(params.year));
    return request<MydataYearSummary>(
      `/mydata/year-summary?${query.toString()}`,
    );
  },

  getMydataDocuments(params: {
    dateFrom?: string;
    dateTo?: string;
    direction?: 'transmitted' | 'received';
    search?: string;
    page?: number;
    pageSize?: number;
  } = {}) {
    const query = new URLSearchParams();
    if (params.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params.dateTo) query.set('dateTo', params.dateTo);
    if (params.direction) query.set('direction', params.direction);
    if (params.search) query.set('search', params.search);
    if (params.page) query.set('page', String(params.page));
    if (params.pageSize) query.set('pageSize', String(params.pageSize));
    const qs = query.toString();
    return request<MydataDocumentsPage>(
      `/mydata/documents${qs ? `?${qs}` : ''}`,
    );
  },

  getMydataDocument(mark: string) {
    return request<MydataDocumentDetail>(
      `/mydata/documents/${encodeURIComponent(mark)}`,
    );
  },

  async downloadMydataDocumentPdf(
    mark: string,
    fileName?: string,
    options?: { previewWindow?: Window | null },
  ): Promise<{ mode: 'file' } | { mode: 'html-viewer'; openUrl: string }> {
    const preview = options?.previewWindow ?? null;
    const headers = new Headers();
    const token = getStoredToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    try {
      const response = await fetch(
        `${API_BASE}/mydata/documents/${encodeURIComponent(mark)}/pdf`,
        { headers },
      );
      if (!response.ok) {
        let message: string = t().common.error;
        try {
          const body = (await response.json()) as {
            message?: string | string[];
          };
          if (typeof body.message === 'string') message = body.message;
          else if (Array.isArray(body.message) && body.message[0]) {
            message = body.message[0];
          }
        } catch {
          /* ignore */
        }
        throw new ApiError(message, response.status);
      }

      const openUrlHeader = response.headers.get('X-Mydata-Open-Url');
      const contentType = response.headers.get('content-type') ?? '';
      if (openUrlHeader || contentType.includes('application/json')) {
        let openUrl = openUrlHeader;
        if (!openUrl) {
          const body = (await response.json()) as { openUrl?: string };
          openUrl = body.openUrl ?? null;
        } else {
          void response.json().catch(() => undefined);
        }
        if (!openUrl) {
          throw new ApiError(t().common.error, response.status);
        }
        if (preview && !preview.closed) {
          preview.location.href = openUrl;
        } else {
          const opened = window.open(openUrl, '_blank', 'noopener,noreferrer');
          if (!opened) {
            const form = document.createElement('form');
            form.method = 'GET';
            form.action = openUrl;
            form.target = '_blank';
            form.rel = 'noopener noreferrer';
            document.body.appendChild(form);
            form.submit();
            form.remove();
          }
        }
        return { mode: 'html-viewer', openUrl };
      }

      preview?.close();
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download =
        fileName ||
        `mydata-${mark}.pdf`.replace(/[^\w.-]+/g, '_');
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      return { mode: 'file' };
    } catch (err) {
      preview?.close();
      throw err;
    }
  },

  getMydataMonthPdfBundlePreview(params: { year: number; month: number }) {
    const query = new URLSearchParams();
    query.set('year', String(params.year));
    query.set('month', String(params.month));
    return request<MydataMonthPdfBundlePreview>(
      `/mydata/documents/pdf-bundle/preview?${query.toString()}`,
    );
  },

  async downloadMydataMonthPdfBundle(params: {
    year: number;
    month: number;
  }): Promise<{ included: number; failed: number }> {
    const headers = new Headers();
    const token = getStoredToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    const query = new URLSearchParams();
    query.set('year', String(params.year));
    query.set('month', String(params.month));
    const response = await fetch(
      `${API_BASE}/mydata/documents/pdf-bundle?${query.toString()}`,
      { headers },
    );
    if (!response.ok) {
      let message: string = t().common.error;
      try {
        const body = (await response.json()) as { message?: string | string[] };
        if (typeof body.message === 'string') message = body.message;
        else if (Array.isArray(body.message) && body.message[0]) {
          message = body.message[0];
        }
      } catch {
        /* ignore */
      }
      throw new ApiError(message, response.status);
    }
    const included = Number(response.headers.get('X-Pdf-Included') ?? 0);
    const failed = Number(response.headers.get('X-Pdf-Failed') ?? 0);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `mydata-pdf-${params.year}-${String(params.month).padStart(2, '0')}.zip`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    return {
      included: Number.isFinite(included) ? included : 0,
      failed: Number.isFinite(failed) ? failed : 0,
    };
  },

  getDashboard(month?: number, year?: number) {
    const query = new URLSearchParams();
    if (month) query.set('month', String(month));
    if (year) query.set('year', String(year));
    const qs = query.toString();
    return request<DashboardData>(`/dashboard${qs ? `?${qs}` : ''}`);
  },
};
